import { Button, Card, CardLevel, Classes, Frow, Heading2, ISelectOption, PaddingLevel, Tooltip } from '@pinpointhq/thumbtack';
import { format } from 'date-fns';
import * as React from 'react';
import ReactTable from 'react-table';
import { AllocationRun, IGeneralAllocationSettings } from '../../../javascript/models';
import useVisibility from '../../shared/hooks/useVisibility';
import humanize from '../../shared/utilities/humanize';
import { useSpraypaintTableNamedParams } from '../shared/hooks/useSpraypaintTable';
import NewPaginationComponent from '../shared/NewPaginationComponent';
import { CreateDrawer } from './CreateDrawer';
import { GeneralDrawer } from './GeneralDrawer';
import { GeneralShowDrawer } from './GeneralShowDrawer';
import { ShowDrawer } from './ShowDrawer';

const REFRESH_INTERVAL = 10000;

export default function Index({
  educationLevels,
  yearsOfGraduation,
  degreeSubjects,
  superAdmin,
  regionOptions,
  workstreamOptions,
  skillOptions,
  currentCycleName,
  generalSettings,
  membershipOptions,
  jobProgressOptions,
}: {
  educationLevels: ISelectOption[];
  yearsOfGraduation: ISelectOption[];
  degreeSubjects: ISelectOption[];
  regionOptions: ISelectOption[];
  superAdmin: boolean;
  workstreamOptions: ISelectOption[];
  skillOptions: ISelectOption[];
  currentCycleName: string;
  generalSettings: IGeneralAllocationSettings;
  membershipOptions: ISelectOption[];
  jobProgressOptions: ISelectOption[];
}) {
  const { tableProps, fetchData } = useSpraypaintTableNamedParams({
    initialScope: AllocationRun.includes(['user']).selectExtra(['cycle_name', 'pinpoint_counts']).order({ created_at: 'desc' }),
    searchIdentifier: 'allocationRuns',
  });
  const { isOpen, handleOpen, handleClose } = useVisibility();
  const [selectedAllocationRun, setSelectedAllocationRun] = React.useState<AllocationRun | null>(null);
  const [generalSettingsToRun, setGeneralSettingsToRun] = React.useState<IGeneralAllocationSettings | null>(null);

  const paginationProps = React.useCallback(() => tableProps, [tableProps]);

  // General allocations run in the background, so keep the list up to date while one is going
  const runs: AllocationRun[] = tableProps.data || [];
  const inProgress = runs.some((run) => ['scheduled', 'running', 'assigning'].includes(run.status));
  React.useEffect(() => {
    if (!inProgress) return;
    const interval = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [inProgress, fetchData]);

  const statusCell = (run: AllocationRun) => {
    const progress = run.output && run.output.progress;
    if (run.status === 'running' && progress) {
      return (
        <div>
          Running: {progress.label} ({progress.done} of {progress.total}), step {progress.step} of {progress.steps}
        </div>
      );
    }
    if (run.status === 'failed') {
      return (
        <Tooltip content={(run.output && run.output.error) || 'Unknown error'}>
          <span>Failed</span>
        </Tooltip>
      );
    }
    if (run.status === 'completed' || (run.generalAllocation && ['assigning', 'assigned'].includes(run.status))) {
      return (
        <span className={Classes.LINK} onClick={() => setSelectedAllocationRun(run)}>
          {humanize(run.status)}
        </span>
      );
    }
    return <div>{humanize(run.status)}</div>;
  };

  const columns = [
    {
      Cell: ({ original }: { original: AllocationRun }) => statusCell(original),
      Header: 'Status',
      accessor: 'status',
      sortable: false,
    },
    {
      Cell: ({ original }: { original: AllocationRun }) => (
        <div>{original.generalAllocation ? `General ${original.cycleName || ''}` : 'Targeted'}</div>
      ),
      Header: 'Type',
      sortable: false,
    },
    {
      Cell: ({ original }: { original: AllocationRun }) => {
        return <div>{original.user ? original.user.fullName : 'Deleted User'}</div>;
      },
      Header: 'Created by',
      sortable: false,
    },
    {
      Cell: ({ original }: { original: AllocationRun }) => {
        return <div>{format(original.createdAt, 'MMMM Do YYYY [at] HH:mm')}</div>;
      },
      Header: 'Created at',
      accessor: 'created_at',
      sortable: true,
    },
  ];

  const generalOptions = {
    currentCycleName,
    membershipOptions,
    jobProgressOptions,
    educationLevels,
    yearsOfGraduation,
    degreeSubjects,
    skillOptions,
  };

  const handleRunAgain = (settings: IGeneralAllocationSettings) => {
    setSelectedAllocationRun(null);
    setGeneralSettingsToRun(settings);
  };

  return (
    <>
      <Card paddingLevel={PaddingLevel.NONE} level={CardLevel.RAISED}>
        <Card paddingLevel={PaddingLevel.REGULAR} level={CardLevel.FILLED}>
          <Frow justifyContent="space-between" alignItems="center">
            <div>
              <Heading2 className="mar-b-0">Allocation Runs</Heading2>
            </div>
            <Frow gutters={8}>
              <div>
                <Button text="New general allocation" onClick={() => setGeneralSettingsToRun(generalSettings)} />
              </div>
              {superAdmin && (
                <div>
                  <Button text="New allocation run" onClick={handleOpen} />
                </div>
              )}
            </Frow>
          </Frow>
        </Card>
        <Card paddingLevel={PaddingLevel.REGULAR} level={CardLevel.NONE}>
          <ReactTable
            {...tableProps}
            columns={columns}
            PaginationComponent={NewPaginationComponent}
            getPaginationProps={paginationProps}
            className="-unthemed"
            minRows={1}
          />
        </Card>
      </Card>
      {selectedAllocationRun && selectedAllocationRun.generalAllocation && (
        <GeneralShowDrawer
          handleClose={() => setSelectedAllocationRun(null)}
          allocationRun={selectedAllocationRun}
          fetchData={fetchData}
          handleRunAgain={handleRunAgain}
        />
      )}
      {selectedAllocationRun && !selectedAllocationRun.generalAllocation && (
        <ShowDrawer
          handleClose={() => setSelectedAllocationRun(null)}
          allocationRun={selectedAllocationRun}
          fetchData={fetchData}
        />
      )}
      {generalSettingsToRun && (
        <GeneralDrawer
          initialSettings={generalSettingsToRun}
          handleClose={() => setGeneralSettingsToRun(null)}
          fetchData={fetchData}
          {...generalOptions}
        />
      )}
      {isOpen && (
        <CreateDrawer
          handleClose={handleClose}
          allocationRun={new AllocationRun({ status: 'scheduled' })}
          educationLevels={educationLevels}
          yearsOfGraduation={yearsOfGraduation}
          degreeSubjects={degreeSubjects}
          fetchData={fetchData}
          regionOptions={regionOptions}
          workstreamOptions={workstreamOptions}
          skillOptions={skillOptions}
        />
      )}
    </>
  );
}
